import {
  ManualUploadCsvRow,
  ManualUploadFileSummary,
} from '@/app/types/manual-upload';
import {
  Configuration,
  type IngestionLogsJsonZipIngestResponseDto,
} from '@evimo/client';
import { IngestionLogsApi } from '@evimo/client/apis';
import { KNOWLEDGE_API_URL } from '~/src/constants/env';

const API_BASE_URL = KNOWLEDGE_API_URL;
const CHUNK_SIZE_BYTES = 5 * 1024 * 1024;
const ingestionLogsApi = new IngestionLogsApi(
  new Configuration({
    basePath: API_BASE_URL,
    credentials: 'include',
  }),
);

const toArchiveUploadSummary = (
  response: IngestionLogsJsonZipIngestResponseDto,
): ManualUploadFileSummary => {
  return {
    uploadId: response.archiveName,
    completed: true,
    receivedChunks: 1,
    totalChunks: 1,
    insertedCount: response.recordCount,
    failedCount: 0,
    processedFileCount: response.jsonlFileCount,
    skippedEntryCount: response.skippedEntryCount,
    errors: [],
    sourceName: response.archiveName,
  };
};

const parseChunkResponse = (responseText: string): ManualUploadFileSummary => {
  return JSON.parse(responseText) as ManualUploadFileSummary;
};

const sendChunk = (
  formData: FormData,
  onProgress: (progress: number) => void,
): Promise<ManualUploadFileSummary> => {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', `${API_BASE_URL}/api/v1/ingestion-logs/chunks`, true);
    request.withCredentials = true;

    request.upload.onprogress = (event) => {
      if (!event.lengthComputable) {
        return;
      }

      onProgress(Math.round((event.loaded / event.total) * 100));
    };

    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        resolve(parseChunkResponse(request.responseText));
        return;
      }

      reject(
        new Error(request.responseText || 'Manual upload request failed.'),
      );
    };

    request.onerror = () => {
      reject(new Error('Network error while uploading the file.'));
    };

    request.send(formData);
  });
};

export const uploadManualFileInChunks = async (params: {
  file: File;
  assetId: string;
  clientName: string;
  onProgress: (progress: number) => void;
}): Promise<ManualUploadFileSummary> => {
  const { file, assetId, clientName, onProgress } = params;
  const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE_BYTES));
  const uploadId = crypto.randomUUID();
  let latestSummary: ManualUploadFileSummary | null = null;

  for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
    const start = chunkIndex * CHUNK_SIZE_BYTES;
    const end = Math.min(start + CHUNK_SIZE_BYTES, file.size);
    const chunk = file.slice(start, end);
    const formData = new FormData();

    formData.append('chunk', chunk, file.name);
    formData.append('uploadId', uploadId);
    formData.append('chunkIndex', String(chunkIndex));
    formData.append('totalChunks', String(totalChunks));
    formData.append('originalFileName', file.name);
    formData.append('asset_id', assetId);
    formData.append('clientName', clientName);

    latestSummary = await sendChunk(formData, (chunkProgress) => {
      const uploadedBytes =
        chunkIndex * CHUNK_SIZE_BYTES +
        Math.round((chunkProgress / 100) * chunk.size);
      onProgress(Math.min(100, Math.round((uploadedBytes / file.size) * 100)));
    });
  }

  if (!latestSummary) {
    throw new Error('Manual upload did not return a summary.');
  }

  onProgress(100);
  return latestSummary;
};

export const uploadManualArchive = async (params: {
  file: File;
  assetId: string;
  clientName: string;
  onProgress: (progress: number) => void;
}): Promise<ManualUploadFileSummary> => {
  const { file, assetId, clientName, onProgress } = params;
  const response =
    await ingestionLogsApi.ingestionLogsJsonZipControllerIngestArchiveRaw({
      assetId,
      clientName,
      archive: file,
    });
  const summary = toArchiveUploadSummary(await response.value());

  onProgress(100);
  return summary;
};

export const uploadMappedCsvRows = async (params: {
  assetId: string;
  clientName: string;
  sourceName: string;
  rows: ManualUploadCsvRow[];
}): Promise<ManualUploadFileSummary> => {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/ingestion-logs/csv-rows`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        asset_id: params.assetId,
        clientName: params.clientName,
        sourceName: params.sourceName,
        rows: params.rows,
      }),
    },
  );

  if (!response.ok) {
    throw new Error(await response.text());
  }

  return (await response.json()) as ManualUploadFileSummary;
};
